import React, { FC, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

import { useStamps } from '../../../../lib/stamps';

type Stamp = {
    id: number,
    number: number,
    text: string,
    date: string,
    genere: string,
    href?: string,
};

type PagerProps = {
    pager: { current: number, prev: number, next: number };
    onPaging: (page: number) => void;
};

const Pager: FC<PagerProps> = ({ pager, onPaging }) => {
    return <>
        <div className="pager">
            {pager.prev > 0 ? (
                <a className="arrow" onClick={() => onPaging(pager.prev)}>&lt;</a>
            ) : (
                <span className="arrow -inactive">&lt;</span>
            )}
            {pager.current > 0 && <span className="current">{pager.current}枚目</span>}
            {pager.next > 0 ? (
                <a className="arrow" onClick={() => onPaging(pager.next)}>&gt;</a>
            ) : (
                <span className="arrow -inactive">&gt;</span>
            )}
        </div>

        <style jsx>{`
        .pager {
            display: flex;
            margin-bottom: 1em;
            color: #ffffff;
        }
        .pager > .current {
            flex-grow: 1;
            font-size: 0.8rem;
            text-align: center;
        }
        .pager > .arrow {
            flex-grow: 0;
            cursor: pointer;
        }
        .pager > .arrow.-inactive {
            visibility: hidden;
        }
        `}</style>
    </>;
};

type Props = {
    id: number;
    spotName: string;
};

export const StampCard: FC<Props> = ({ id, spotName }) => {
    const [stamps, setStamps] = useState<(Stamp | null)[]>([]);
    const [loading, setLoading] = useState(false);
    const [stamp, setStamp] = useState<Stamp | null>(null);
    const [pager, setPager] = useState({ current: 0, prev: 0, next: 0 });
    const [name, setName] = useState('');
    const { getCard } = useStamps();

    const stampIcons = {
        '飲食店': '/img/map_stamp_icons/stamp-rest.png',
        'ヘアサロン': '/img/map_stamp_icons/stamp-cut.png',
        'スナックキャンディ': '/img/map_stamp_icons/stamp-candy.png',
        '宿泊': '/img/map_stamp_icons/stamp-hotel.png',
        'その他': '/img/map_stamp_icons/stamp-other.png',
        '自販機': '/img/map_stamp_icons/stamp-vend.png',
        '居酒屋・バー': '/img/map_stamp_icons/stamp-bar.png',
        'レストラン・カフェ': '/img/map_stamp_icons/stamp-cafe.png',
        'スイーツ': '/img/map_stamp_icons/stamp-sweets.png',
        '健康': '/img/map_stamp_icons/stamp-health.png',
        '医療・福祉': '/img/map_stamp_icons/stamp-medical.png',
        '小売': '/img/map_stamp_icons/stamp-retail.png',
        'エンタメ・レジャー': '/img/map_stamp_icons/stamp-entertainment.png',
        '習いごと': '/img/map_stamp_icons/stamp-education.png',
        'ビューティ': '/img/map_stamp_icons/stamp-beauty.png',
        'レンタルスペース': '/img/map_stamp_icons/stamp-rentalspace.png',
        'ビジネス': '/img/map_stamp_icons/stamp-buisiness.png',
        '保育園・幼稚園': '/img/map_stamp_icons/stamp-nursery.png',
        '農業': '/img/map_stamp_icons/stamp-agri.png',
    };

    useEffect(() => {
        async function initialize() {
            await paging();
        }
        initialize();
    }, []);

    const formatDate = (date: string) => {
        const d = new Date(date);
        const yyyy = d.getFullYear();
        const mm = ('0' + (d.getMonth() + 1)).slice(-2);
        const dd = ('0' + d.getDate()).slice(-2);
        return `${yyyy}.${mm}.${dd}`;
    };

    const paging = async (page?: number) => {
        const { card, stamps, pager } = await getCard(id, page);
        if (card && card.type === 1) {
            setName(stamps[0].spot.name);
        };
        setPager(pager);
        setStamps(fillEmptyCells(
            stamps,
            card === null || card === undefined,
        ));
    };

    const fillEmptyCells = (stamps: [], isMixed = false) => {
        if (stamps.length === 0) {
            return (new Array(10)).fill(null);
        }
        return stamps.map((stamp: any) => {
            if (!stamp) {
                return null;
            }
            const s = {
                id: stamp.id,
                number: stamp.spot_id,
                text: isMixed ? stamp.spot.name : null,
                date: formatDate(stamp.created),
                href: isMixed ? `/map/stampcard/${stamp.spot_id}` : null,
                genre: stamp.spot.spot_genre.genre,
            };
            return stamp ? s : null;
        });
    };

    return (
        <div className="wrap">
            <div className="title">
                <h1>
                    <span className="main">{spotName}</span><br /><span className="sub">スタンプカード</span>
                </h1>
            </div>
            <div className="qr-button-wrap">
                <Link to="/map/qr" style={{ textDecoration: "none" }}>
                    <a className="qr-button">
                        <img src="/img/qr_white.png" />
                        QRコードで読み込み
                    </a>
                </Link>
            </div>

            {pager && <Pager pager={pager} onPaging={paging} />}

            <div className="stampcard-wrap">
                <div className="stampcard">
                    {stamps.map((stamp, idx) => (
                        <div className="stamp">
                            {!stamp ? (
                                <span className="number">{idx + 1}</span>
                            ) : (
                                <a href={stamp.href}>
                                    {/* @ts-ignore */}
                                    <img className="mark" src={stampIcons[stamp.genre]} />
                                </a>
                            )}
                            {!stamp?.href && <span className="date">{stamp?.date}</span>}
                            {stamp?.href && (
                                <a className="text-wrapper" href={stamp?.href}>
                                    <span className="text">{stamp?.text}</span>
                                    <span className="date">{stamp?.date}</span>
                                </a>
                            )}
                        </div>
                    ))}
                </div>
            </div>
            <div className="manage-stamp-button-container">
                <a className="manage-stamp-button" href={`/map/stamps/${id}/edit`}>
                    スタンプを管理する
                </a>
            </div>
            <div className="notice-wrap">
                <span>＊お店ごとのスタンプは1日1回取得できます。</span><br />
                <span>＊位置情報を許可して店内でスタンプを押してください。</span>
            </div>

            <style jsx>{`
                .title {
                    display: flex;
                    justify-content: center;
                    padding-top: 20px;
                    padding-bottom: 20px;
                    text-align: center;
                    color: #ffffff;
                }
                .title > h1 {
                    margin: 0;
                    line-height: 1;
                }
                .em {
                    font-size: 1.2rem;
                    font-weight: bold;
                }
                .small {
                    font-size: 0.7rem;
                }
                .main {
                    font-size: 1.2rem;
                }
                .sub {
                    font-size: 0.9rem;
                }
                .wrap {
                    padding: 15px;
                    background: #f1bd3f;
                }
                .wrap.-spot {
                    background: #f1bd3f;
                }
                .wrap.-spot > .title::before,
                .wrap.-spot > .title::after {
                    width: 0;
                }
                .wrap.-spot .stamp {
                    border: 1px dashed #f1bd3f;
                }
                .wrap.-spot .number {
                    color: #f7de9f;
                }
                .stampcard-wrap {
                    border-radius: 30px;
                    overflow: hidden;
                }
                .stampcard {
                    display: grid;
                    grid-template-columns: repeat(2, minmax(50px, 1fr));
                    margin: -4px;
                    border: 3px solid transparent;
                }
                .stamp {
                    display: flex;
                    align-items: center;
                    padding: 0.5rem;
                    min-height: 120px;
                    justify-content: center;
                    flex-direction: column;
                    border: 1px dashed #ed7c4f;
                    background: #ffffff;
                    position: relative;
                }
                .remove-stamp-button {
                    position: absolute;
                    width: 25px;
                    height: 25px;
                    top: 10px;
                    left: 10px;
                    background: none;
                    border: none;
                }
                .remove-stamp-button > img {
                    width: 25px;
                    height: 25px;
                }
                .text-wrapper {
                    text-decoration: none;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    flex-direction: column;
                }
                .text,
                a.text:link,
                a.text:visited,
                a.text:hover,
                a.text:active {
                    color: #ed7c4f;
                    font-size: 0.8rem;
                    font-weight: bold;
                    text-decoration: underline;
                }
                .date {
                    color: #ed7c4f;
                    font-size: calc(0.8rem - 1px);
                }
                .number {
                    color: #f7cab8;
                    font-size: 2em;
                }
                .mark {
                    display:block;
                    transition: ease-out 1s;
                }
                .mark.-big {
                    opacity: 0.2;
                    transform: scale(3, 3);
                }
                .qr-button-wrap {
                    width: 100%;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                .qr-button {
                    background: rgb(255, 146, 64);
                    color: rgb(255, 255, 255);
                    padding: 6px 20px;
                    margin-bottom: 20px;
                    border-radius: 30px;
                    font-size: 14px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }
                .qr-button > img {
                    margin-right: 8px;
                }
                .notice-wrap {
                    color: #fff;
                    font-size: 14px;
                    margin-top: 10px;
                }
                .manage-stamp-button-container {
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    margin: 10px 0px;
                }
                .manage-stamp-button {
                    color: #fff;
                    font-size: 15px;
                    text-align: center;
                    background: none;
                    border: none;
                }
            `}</style>
        </div>
    );
};
